import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ShoppingBag, Download, Tag } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface MarketplaceItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  industryTags: string[];
  useCaseTags: string[];
  version: number;
  author: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function WorkflowMarketplacePage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [importingSlug, setImportingSlug] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [importDialogSlug, setImportDialogSlug] = useState<string | null>(null);
  const [importWorkflowId, setImportWorkflowId] = useState('');

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<MarketplaceItem[]>('/workflow-marketplace');
      setItems(res.data);
    } catch {
      flash('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function importItem() {
    if (!importDialogSlug || !importWorkflowId.trim()) return;
    setImportingSlug(importDialogSlug);
    try {
      await apiClient.post(`/workflow-marketplace/${importDialogSlug}/import`, {
        workflowId: importWorkflowId.trim(),
      });
      flash(t('marketplace.imported'));
      setImportDialogSlug(null);
      setImportWorkflowId('');
    } catch {
      flash('Failed to import template');
    } finally {
      setImportingSlug(null);
    }
  }

  const filtered = items.filter(
    (item) =>
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.description?.toLowerCase().includes(search.toLowerCase()) ||
      item.industryTags.some((t) => t.toLowerCase().includes(search.toLowerCase())) ||
      item.useCaseTags.some((t) => t.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Import dialog */}
      {importDialogSlug && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border bg-card p-6 shadow-xl">
            <h3 className="text-lg font-semibold">{t('marketplace.import')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter the Workflow ID to import this template as a new draft version.
            </p>
            <div className="mt-4">
              <label className="mb-1 block text-xs font-medium">Workflow ID (UUID)</label>
              <Input
                value={importWorkflowId}
                onChange={(e) => setImportWorkflowId(e.target.value)}
                placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setImportDialogSlug(null)}>
                Cancel
              </Button>
              <Button
                onClick={importItem}
                disabled={importingSlug === importDialogSlug || !importWorkflowId.trim()}
              >
                <Download className="mr-1.5 h-4 w-4" />
                {importingSlug === importDialogSlug ? 'Importing...' : t('marketplace.import')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">{t('marketplace.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('marketplace.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="flex gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`${t('marketplace.industry_filter')} / ${t('marketplace.use_case_filter')}...`}
          className="max-w-xs"
        />
      </div>

      {/* Loading */}
      {loading && (
        <p className="text-sm text-muted-foreground">Loading...</p>
      )}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div className="rounded-xl border bg-card px-6 py-12 text-center">
          <ShoppingBag className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">{t('marketplace.empty')}</p>
        </div>
      )}

      {/* Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((item) => (
          <div key={item.id} className="rounded-xl border bg-card p-5">
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-semibold">{item.name}</h3>
              <span className="shrink-0 text-xs text-muted-foreground">
                {t('marketplace.version_label')}{item.version}
              </span>
            </div>
            {item.description && (
              <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">
                {item.description}
              </p>
            )}

            {/* Tags */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {item.industryTags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-0.5 rounded-full bg-blue-900/40 px-2 py-0.5 text-[10px] font-medium text-blue-200"
                >
                  <Tag className="h-2.5 w-2.5" />
                  {tag}
                </span>
              ))}
              {item.useCaseTags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t('marketplace.by')} {item.author}
              </span>
              <Button
                size="sm"
                onClick={() => setImportDialogSlug(item.slug)}
              >
                <Download className="mr-1 h-3.5 w-3.5" />
                {t('marketplace.import')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
